"use client";

import * as React from "react";

import { cn } from "@/lib/utils";

interface ToastProps {
  title?: string;
  description?: React.ReactNode;
  variant?: "default" | "destructive";
}

const ToastContext = React.createContext<{
  toast: (props: ToastProps) => void;
} | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastProps[]>([]);

  const toast = React.useCallback((props: ToastProps) => {
    setToasts((prev) => [...prev, props]);

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setToasts((prev) => prev.slice(1));
    }, 5000);
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed right-4 bottom-4 z-50 flex flex-col gap-2">
        {toasts.map((toast, index) => (
          <div
            key={index}
            className={cn(
              "rounded-lg border p-4 shadow-lg",
              toast.variant === "destructive"
                ? "text-destructive-foreground border-destructive bg-destructive"
                : "border-border bg-background"
            )}
          >
            {toast.title && <div className="font-semibold">{toast.title}</div>}
            {toast.description && (
              <div className="mt-1 text-sm">{toast.description}</div>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }

  return context;
}

// Add this to your layout.tsx
// <ToastProvider>{children}</ToastProvider>
